import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'
import { initials } from '@/utils/format'

const avatarVariants = cva(
  'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold select-none',
  {
    variants: {
      size: {
        xs: 'size-6 text-2xs',
        sm: 'size-8 text-xs',
        md: 'size-10 text-sm',
        lg: 'size-14 text-lg',
        xl: 'size-20 text-2xl',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

// Deterministic pastel palette so the same person always gets the same colour.
const palettes = [
  'bg-brand-100 text-brand-700',
  'bg-success-100 text-success-700',
  'bg-warning-100 text-warning-700',
  'bg-info-100 text-info-700',
  'bg-danger-100 text-danger-700',
  'bg-violet-100 text-violet-700',
  'bg-teal-100 text-teal-700',
  'bg-pink-100 text-pink-700',
]

function paletteFor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return palettes[Math.abs(hash) % palettes.length] ?? 'bg-brand-100 text-brand-700'
}

interface AvatarProps extends VariantProps<typeof avatarVariants> {
  name: string
  src?: string | null
  className?: string
}

export function Avatar({ name, src, size, className }: AvatarProps) {
  return (
    <span
      className={cn(avatarVariants({ size }), !src && paletteFor(name), className)}
      aria-hidden={src ? undefined : true}
    >
      {src ? <img src={src} alt={name} className="size-full object-cover" loading="lazy" /> : initials(name)}
    </span>
  )
}
